import { useState } from "react";
import { UploadSimple, DownloadSimple, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const COLUMNS = ["name", "client_type", "contact_person", "phone", "email", "address", "city", "gstin", "group_name", "tags", "notes"];
const SAMPLE = `${COLUMNS.join(",")}\nRamesh Agrawal,Individual,Ramesh Agrawal,9826100000,ramesh@example.com,"Shankar Nagar, Raipur",Raipur,,Agrawal Family,Repeat Client|HNI,Referred by SBI Pandri\n`;

export default function ImportClients() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "client-import-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.created} clients imported`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="import-clients-page">
      <PageHeader overline="Clients" title="Import Clients" subtitle="Bring existing client records in from Excel or CSV — duplicates by name are skipped automatically" />

      <Card className="p-6 border-border">
        <SectionTitle icon={DownloadSimple}>1. Get the template</SectionTitle>
        <p className="text-sm text-muted-foreground">Download the CSV template, fill it in Excel, then save as CSV (UTF-8). Separate multiple tags with a pipe: <span className="font-mono text-xs">HNI|Repeat Client</span></p>
        <Button variant="outline" className="rounded-full mt-4" onClick={downloadTemplate} data-testid="download-template-btn">
          <DownloadSimple size={16} className="mr-1" /> Download CSV Template
        </Button>
        <div className="mt-4 rounded-md bg-muted border border-border p-3 overflow-x-auto">
          <code className="text-[11px] font-mono text-muted-foreground whitespace-pre">{COLUMNS.join(", ")}</code>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <SectionTitle icon={UploadSimple}>2. Upload your file</SectionTitle>
        <label className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/40 py-10 cursor-pointer hover:border-accent transition-colors duration-200" data-testid="import-dropzone">
          <UploadSimple size={26} className="text-accent" />
          <span className="text-sm text-primary font-medium">{file ? file.name : "Choose a CSV file"}</span>
          <span className="text-xs text-muted-foreground">Max 2 MB · UTF-8 CSV</span>
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }} data-testid="import-file-input" />
        </label>
        <Button onClick={upload} disabled={busy || !file} className="rounded-full bg-primary hover:bg-[#113A64] mt-4" data-testid="import-submit-btn">
          {busy ? "Importing…" : "Import Clients"}
        </Button>
      </Card>

      {result && (
        <Card className="p-6 border-border" data-testid="import-result">
          <SectionTitle icon={CheckCircle}>Import summary</SectionTitle>
          <div className="grid grid-cols-3 gap-4">
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Created</div><div className="font-display font-black text-2xl text-emerald-600 tabular">{result.created}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Skipped (duplicate)</div><div className="font-display font-black text-2xl text-amber-600 tabular">{result.skipped}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Errors</div><div className="font-display font-black text-2xl text-rose-600 tabular">{result.errors.length}</div></div>
          </div>
          {result.errors.length > 0 && (
            <div className="mt-4 space-y-1">
              {result.errors.map((e) => (
                <div key={e} className="text-xs text-rose-700 inline-flex items-center gap-1.5"><Warning size={13} /> {e}</div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
